import { takeEvery } from 'redux-saga';
import { take, put, call, fork, select } from 'redux-saga/effects';
import { browserHistory } from 'react-router';
import _get from 'lodash/get';

import { BOOKING_TIME_CONFLICT } from '../constants/apiErrorCodes';
import { CONFLICT } from '../constants/httpStatusCodes';
import {
    CREATE_BOOKING,
    FETCH_AVAILABLE_PLANT,
    FETCH_AVAILABLE_PLANT_TYPES,
    DATE_TIME_FORMAT,
} from '../constants';
import * as loadingGraphics from '../constants/loading';

import * as api from '../services/api';
import * as environmentActions from '../actions/environment';
import { getNewBooking, getAuthedUser, getTravelReason } from '../selectors';
import { bookingUpdated } from '../actions/bookings';
import { setAvailablePlant } from '../actions/newBooking';
import { callApi, getConflictReasonText } from './common';
import { fetchEntities } from './entities';

function* watchCreateBooking() {
    while(true) {
        yield take(CREATE_BOOKING);
        const booking = yield select(getNewBooking);
        const user = yield select(getAuthedUser);

        const response = yield call(callApi, {
            apiMethod: api.createBooking,
            apiData: {
                startTime: booking.startTime.format(DATE_TIME_FORMAT),
                endTime: booking.endTime.format(DATE_TIME_FORMAT),
                pickupLocationId: booking.pickupLocationId,
                journeyToLocationId: booking.journeyToLocationId,
                customJourneyToLocation: booking.otherJourneyToLocation,
                travelReasonId: booking.travelReasonId,
                customTravelReason: booking.otherTravelReason,
                costCentreId: booking.costCentreId,
                plantId: booking.plantId,
                privateUse: booking.privateUse,
                driverId: user.personId,
            },
            loading: {
                graphic: loadingGraphics.GENERIC,
                loadingText: `Creating new booking...`,
            },
            postSnackbarText: `Booking created successfully.`,
            onError: handleCreateBookingError,
        });

        if (response.ok) {
            yield put(bookingUpdated(response.body));
            browserHistory.push(`/bookings`);
            yield call(fetchEntities); //updates any newly created locations
        }
    }
}

function* handleCreateBookingError(response) {
    if (response.status === CONFLICT) {
        let error;
        if (response.body.errorCode === BOOKING_TIME_CONFLICT) {
            response.handled = true;
            // Display error with reasons for conflict
            const conflictText = getConflictReasonText(response.body.data.reasons);
            error = [
                `The selected times are unavailable:`,
                conflictText,
            ];
        } else {
            error = [
                response.body.message,
            ];
        }

        yield put(environmentActions.setError(error));
    }
}

function* watchFetchAvailablePlant() {
    while(true) {
        let { startTime, endTime, plantTypeId, driverId, locationId } = yield take(FETCH_AVAILABLE_PLANT);
        const apiQuery = {
            startTime: startTime.format(DATE_TIME_FORMAT),
            endTime: endTime.format(DATE_TIME_FORMAT),
            driverId,
            locationId,
        };

        if (plantTypeId)
            apiQuery.plantTypeIds = [plantTypeId];

        const response = yield call(callApi, {
            apiMethod: api.fetchAvailablePlant,
            apiQuery,
            loading: {
                graphic: loadingGraphics.getPlantGraphic(),
                loadingText: `Finding available plant...`,
            },
        });

        if (response.ok) {
            const { unavailableReasons, availablePlantIds } = response.body;
            if (unavailableReasons.length > 0) {
                yield put(environmentActions.setError([
                    `We are unable to make this booking:`,
                    ...unavailableReasons,
                ]));
            } else if (availablePlantIds.length == 0) {
                yield put(environmentActions.setError([
                    `There are no available plant matching your criteria.`,``
                ]));
            } else {
                yield put(setAvailablePlant(availablePlantIds));
            }
        }
    }
}

function* watchFetchAvailablePlantTypes() {
    yield takeEvery(FETCH_AVAILABLE_PLANT_TYPES, function*(data) {
        if (data.availablePlantTypeIds.length === 0) {
            yield put(environmentActions.setError([
                `No plant types found, you will be unable to create bookings until you have plant types assigned.`,
                ` Please talk to your booking administrator.`
            ]));
        }
    });
}

export {
    watchFetchAvailablePlantTypes,
    watchFetchAvailablePlant,
    watchCreateBooking,
};



// WEBPACK FOOTER //
// ./scripts/sagas/createBooking.js